// 垂直捲動容器 — 對應 Unity UGUI 的 ScrollRect（Viewport + Content，vertical only、無捲軸條）。
//
// 用矩形 Graphics 當遮罩裁切 Viewport、另一層透明 Graphics 當事件捕捉層接收 wheel／拖曳。
// 呼叫端把內容加進 `content`，排完版後回報內容總高（setContentHeight）即可捲動。
// 位置一律以「螢幕座標」設定（本容器不做自身位移），與專案其他 HUD view 的慣例一致。

import { Container, Graphics, type FederatedPointerEvent, type FederatedWheelEvent } from 'pixi.js'

export class ScrollColumn extends Container {
  /** 內容層：呼叫端把區塊加進來，並自行排出 y 座標（相對內容頂端 0）。 */
  readonly content = new Container()

  private readonly catcher = new Graphics()
  private readonly clip = new Graphics()

  private viewTop = 0
  private viewHeight = 0
  private contentHeight = 0
  private scrollY = 0
  private dragging = false
  private dragStartPointerY = 0
  private dragStartScrollY = 0
  /** 拖曳判定門檻（px）：小於此位移視為點擊，不吃掉子元件的 tap。 */
  private dragged = false

  constructor() {
    super()
    this.content.mask = this.clip
    this.content.eventMode = 'static'
    this.catcher.eventMode = 'static'
    this.eventMode = 'static'
    this.addChild(this.catcher, this.content, this.clip)

    // ⚠️ 滾輪監聽必須掛在**本容器**而非 `catcher`：PixiJS v8 的 hitTestRecursive 對 `passive` 子節點會短路
    //    （只要幾何上蓋住該點就回傳空陣列並中止搜尋），使 hit target 變成 `content`；`catcher` 是它的
    //    兄弟節點，永遠輪不到。掛在本容器上，兩種情形（命中 catcher 或命中 content）都會冒泡上來。
    this.on('wheel', (event: FederatedWheelEvent) => {
      this.scrollY -= event.deltaY
      this.applyScroll()
    })
    // 同理，拖曳起點也掛在本容器（`catcher`／`content` 命中時都會冒泡上來）。
    this.on('pointerdown', (event: FederatedPointerEvent) => this.beginDrag(event))
    this.on('globalpointermove', (event: FederatedPointerEvent) => {
      if (!this.dragging) return
      const delta = event.globalY - this.dragStartPointerY
      if (Math.abs(delta) > 4) this.dragged = true
      this.scrollY = this.dragStartScrollY + delta
      this.applyScroll()
    })
    this.on('pointerup', () => (this.dragging = false))
    this.on('pointerupoutside', () => (this.dragging = false))
  }

  /** 這一次互動是否為拖曳（供子元件的 tap 處理器判斷要不要忽略）。 */
  get isDragScroll(): boolean {
    return this.dragged
  }

  /** 設定可視區（螢幕座標）；內容左緣即 x，內容由 y 開始向下排。 */
  setViewport(x: number, y: number, width: number, height: number): void {
    this.viewTop = y
    this.viewHeight = height
    this.catcher.clear().rect(x, y, width, height).fill({ color: 0x000000, alpha: 0.0001 })
    this.clip.clear().rect(x, y, width, height).fill(0xffffff)
    this.content.x = x
    this.applyScroll()
  }

  /** 回報內容總高（排版後呼叫），據此決定可捲動範圍。 */
  setContentHeight(height: number): void {
    this.contentHeight = height
    this.applyScroll()
  }

  /** 捲回頂端（每次開啟時呼叫）。 */
  reset(): void {
    this.scrollY = 0
    this.dragged = false
    this.applyScroll()
  }

  private beginDrag(event: FederatedPointerEvent): void {
    this.dragging = true
    this.dragged = false
    this.dragStartPointerY = event.globalY
    this.dragStartScrollY = this.scrollY
  }

  /** 夾住捲動量（內容比可視區短時不可捲）並套用到內容位置。 */
  private applyScroll(): void {
    const minScroll = Math.min(0, this.viewHeight - this.contentHeight)
    this.scrollY = Math.max(minScroll, Math.min(0, this.scrollY))
    this.content.y = this.viewTop + this.scrollY
  }
}
